import React, { useEffect, useState, useCallback } from "react";
import { Row, Col, Tab, Nav } from "react-bootstrap";
import Modal from "react-modal";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import Select from "react-select";
import PageHeadingNew from "../common-components/custom-comps/PageHeadingNew";
import TableSkeleton from "../common-components/TableSkeleton";
import PaymentLinkTable from "../payment-link-system/PaymentLinkTable";
import FilterContainer from "../common-components/filters/filter-container";
import CustomDropDownFilter from "../common-components/filters/CustomDropDownFilter";
import SearchFilter from "../common-components/filters/search-filter";
import ModalHeadAndClose from "../payouts-wtf/common/ModalHeadAndClose";
import { showConfirmAlert } from "../components-react/ReactAlert";
import { CallIcon, EmailIcon } from "../../images/icons/icons";
import { BASE_URL } from "../../data/BaseUrl";
import "../../styles/staff-onboarding/housekeeping-onboard.css";

const HousekeepingOnboardStructure = ({ userInfoData }) => {
  const TOKEN = userInfoData?.token;

  // Helper function to convert 24-hour to 12-hour format
  const formatTime12Hour = (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Get first or last check-in from attendance_logs
  const getFirstAndLastCheckin = (attendanceLogs, type) => {
    if (!attendanceLogs || attendanceLogs.length === 0) {
      return "N/A";
    }
    if (type === "first") {
      return formatTime12Hour(attendanceLogs[0]?.time);
    } else {
      return formatTime12Hour(attendanceLogs[attendanceLogs.length - 1]?.time);
    }
  };

  // Get first check-in in 24-hour format for late calculation
  const getFirstCheckin24Hour = (attendanceLogs) => {
    if (!attendanceLogs || attendanceLogs.length === 0) {
      return null;
    }
    return attendanceLogs[0]?.time;
  };

  // Calculate time difference in minutes
  const calculateTimeDifference = (shiftTime, checkinTime) => {
    if (!shiftTime || !checkinTime) return null;
    const shift = new Date(`2000-01-01 ${shiftTime}`);
    const checkin = new Date(`2000-01-01 ${checkinTime}`);
    const differenceMs = checkin.getTime() - shift.getTime();
    return Math.floor(differenceMs / (1000 * 60));
  };

  // Get late/early status
  const getLateStatus = (attendanceLogs, shiftStartTime) => {
    const firstCheckin = getFirstCheckin24Hour(attendanceLogs);
    if (!firstCheckin) {
      return { text: "N/A", isLate: null };
    }
    const diffMinutes = calculateTimeDifference(shiftStartTime, firstCheckin);
    if (diffMinutes === null) {
      return { text: "N/A", isLate: null };
    }
    if (diffMinutes === 0) {
      return { text: "On Time", isLate: false };
    } else if (diffMinutes > 0) {
      const hours = Math.floor(diffMinutes / 60);
      const mins = diffMinutes % 60;
      const duration = hours > 0 ? `${hours} Hours ${mins}` : `${mins}`;
      return { text: `${duration} Min Late`, isLate: true };
    } else {
      const absDiff = Math.abs(diffMinutes);
      const hours = Math.floor(absDiff / 60);
      const mins = absDiff % 60;
      const duration = hours > 0 ? `${hours} Hours ${mins}` : `${mins}`;
      return { text: `${duration} Min Early`, isLate: false };
    }
  };

  // Tab state
  const [activeTab, setActiveTab] = useState("housekeeping");

  // Loading states
  const [loading, setLoading] = useState(false);
  const [vendorLoading, setVendorLoading] = useState(false);

  // Data states
  const [housekeepingData, setHousekeepingData] = useState([]);
  const [vendorData, setVendorData] = useState([]);
  const [shiftData, setShiftData] = useState([]);
  const [gymData, setGymData] = useState([]);

  // Pagination
  const [isPage, setIsPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(10);
  const [pagination, setPagination] = useState(null);

  // Vendor pagination
  const [vendorPage, setVendorPage] = useState(0);
  const [vendorPageLimit, setVendorPageLimit] = useState(10);
  const [vendorPagination, setVendorPagination] = useState(null);

  // Filters
  const [searchInput, setSearchInput] = useState("");
  const [gymFilter, setGymFilter] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");

  // Track initial data load to prevent unnecessary refetches on tab switch
  const [housekeepingLoaded, setHousekeepingLoaded] = useState(false);
  const [vendorsLoaded, setVendorsLoaded] = useState(false);

  // Modal states
  const [housekeepingModalOpen, setHousekeepingModalOpen] = useState(false);
  const [vendorModalOpen, setVendorModalOpen] = useState(false);
  const [shiftModalOpen, setShiftModalOpen] = useState(false);
  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
  const [vendorDeleteModalOpen, setVendorDeleteModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editData, setEditData] = useState(null);

  // Vendor delete state
  const [vendorToDelete, setVendorToDelete] = useState(null);
  const [housekeeperCount, setHousekeeperCount] = useState(0);
  const [transferVendorId, setTransferVendorId] = useState("");

  // Attendance history state
  const [attendanceData, setAttendanceData] = useState([]); // For calendar view
  const [attendanceLogs, setAttendanceLogs] = useState([]); // For stats calculation
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [attendanceStats, setAttendanceStats] = useState({
    totalShifts: 0,
    totalCheckins: 0,
    onTime: 0,
    late: 0,
  });
  const [calendarViewDate, setCalendarViewDate] = useState(moment());
  const [selectedDayDetails, setSelectedDayDetails] = useState(null);
  const [dayDetailsModalOpen, setDayDetailsModalOpen] = useState(false);

  // Form states
  const initialHousekeepingForm = {
    name: "",
    mobile: "",
    gym_id: "",
    vendor_id: "",
    shift_ids: [],
    address: "",
    salary: "",
  };
  const [housekeepingForm, setHousekeepingForm] = useState(initialHousekeepingForm);

  const initialVendorForm = {
    name: "",
    mobile: "",
    email: "",
    agency_name: "",
    address: "",
    status: "active",
  };
  const [vendorForm, setVendorForm] = useState(initialVendorForm);

  const initialShiftForm = {
    shift_details: [{ shift_name: "", start_time: "", end_time: "", status: "active" }],
  };
  const [shiftForm, setShiftForm] = useState(initialShiftForm);

  // Fetch Gyms
  const fetchGyms = useCallback(async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/gym?page=1&limit=500&status=active&is_device_id=1`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${TOKEN}` },
        }
      );
      const data = await response.json();
      if (data?.data) {
        setGymData(data.data);
      }
    } catch (error) {
      console.error("Error fetching gyms:", error);
    }
  }, [TOKEN]);

  // Fetch Vendors
  const fetchVendors = useCallback(async () => {
    setVendorLoading(true);
    try {
      const response = await fetch(
        `${BASE_URL}/staff/vendor?page=${vendorPage + 1}&limit=${vendorPageLimit}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${TOKEN}` },
        }
      );
      const data = await response.json();
      if (data?.data) {
        setVendorData(data.data);
        setVendorPagination(data.pagination || []);
        setVendorsLoaded(true);
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
      toast.error("Failed to fetch vendors");
    } finally {
      setVendorLoading(false);
    }
  }, [TOKEN, vendorPage, vendorPageLimit]);

  // Fetch Shifts
  const fetchShifts = useCallback(async () => {
    try {
      const response = await fetch(`${BASE_URL}/staff/housekeeping/shift`, {
        method: "GET",
        headers: { Authorization: `Bearer ${TOKEN}` },
      });
      const data = await response.json();
      if (data?.data) {
        setShiftData(data.data);
      }
    } catch (error) {
      console.error("Error fetching shifts:", error);
    }
  }, [TOKEN]);

  // Fetch Housekeeping Staff
  const fetchHousekeeping = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${BASE_URL}/staff/housekeeping?page=${isPage + 1}&limit=${pageLimit}`;
      if (searchInput) url += `&search=${searchInput}`;
      if (gymFilter) url += `&gym_id=${gymFilter}`;
      if (vendorFilter) url += `&vendor_id=${vendorFilter}`;

      const response = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${TOKEN}` },
      });
      const data = await response.json();
      if (data?.data) {
        setHousekeepingData(data.data);
        setPagination(data.pagination || []);
        setHousekeepingLoaded(true);
      }
    } catch (error) {
      console.error("Error fetching housekeeping:", error);
      toast.error("Failed to fetch housekeeping staff");
    } finally {
      setLoading(false);
    }
  }, [TOKEN, isPage, pageLimit, searchInput, gymFilter, vendorFilter]);

  // Initial fetch - load gyms, shifts, vendors (for dropdowns) and housekeeping (default tab)
  useEffect(() => {
    fetchGyms();
    fetchShifts();
    fetchVendors(); // For dropdown filter
    fetchHousekeeping(); // Default tab data
  }, []); // Only run once on mount

  // Refetch housekeeping when filters or pagination change (not on tab switch)
  useEffect(() => {
    if (housekeepingLoaded) {
      fetchHousekeeping();
    }
  }, [isPage, pageLimit, searchInput, gymFilter, vendorFilter, fetchHousekeeping]);

  // Refetch vendors only when pagination changes (not on tab switch)
  useEffect(() => {
    if (vendorsLoaded) {
      fetchVendors();
    }
  }, [vendorPage, vendorPageLimit, fetchVendors]);

  // Add Vendor
  const handleAddVendor = async () => {
    if (!vendorForm.name || !vendorForm.mobile) {
      toast.error("Name and Mobile are required");
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/staff/vendor`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${TOKEN}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(vendorForm),
      });
      const data = await response.json();
      if (data?.status) {
        toast.success("Vendor added successfully");
        setVendorModalOpen(false);
        setVendorForm(initialVendorForm);
        fetchVendors();
      } else {
        toast.error(data?.message || "Failed to add vendor");
      }
    } catch (error) {
      console.error("Error adding vendor:", error);
      toast.error("Failed to add vendor");
    }
  };

  // Add/Update Housekeeping
  const handleSaveHousekeeping = async () => {
    if (!housekeepingForm.name || !housekeepingForm.mobile || !housekeepingForm.gym_id || !housekeepingForm.vendor_id) {
      toast.error("Name, Mobile, Gym and Vendor are required");
      return;
    }

    try {
      const url = isEdit
        ? `${BASE_URL}/staff/housekeeping`
        : `${BASE_URL}/staff/housekeeping/add`;
      const method = isEdit ? "PUT" : "POST";

      // Convert salary to number before sending
      const formData = {
        ...housekeepingForm,
        salary: parseInt(housekeepingForm.salary) || 0,
      };

      const body = isEdit
        ? { ...formData, uid: editData?.uid }
        : formData;

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${TOKEN}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (data?.status) {
        toast.success(isEdit ? "Updated successfully" : "Added successfully");
        setHousekeepingModalOpen(false);
        setHousekeepingForm(initialHousekeepingForm);
        setIsEdit(false);
        setEditData(null);
        fetchHousekeeping();
      } else {
        toast.error(data?.message || "Operation failed");
      }
    } catch (error) {
      console.error("Error saving housekeeping:", error);
      toast.error("Operation failed");
    }
  };

  // Delete Housekeeping
  const handleDeleteHousekeeping = async (uid) => {
    const handleConfirm = async () => {
      try {
        const response = await fetch(`${BASE_URL}/staff/housekeeping?uid=${uid}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${TOKEN}` },
        });
        const data = await response.json();
        if (data?.status) {
          toast.success("Deleted successfully");
          fetchHousekeeping();
        } else {
          toast.error(data?.message || "Failed to delete");
        }
      } catch (error) {
        console.error("Error deleting:", error);
        toast.error("Failed to delete");
      }
    };
    showConfirmAlert(handleConfirm, "Are you sure you want to delete this staff member?");
  };

  // Delete Vendor - Check for associated housekeepers first
  const handleDeleteVendor = async (vendor) => {
    try {
      // First check how many housekeepers are associated
      const response = await fetch(
        `${BASE_URL}/staff/vendor?uid=${vendor.uid}&action=check`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${TOKEN}` },
        }
      );
      const data = await response.json();

      if (data?.status) {
        setVendorToDelete(vendor);
        setHousekeeperCount(data.housekeepers_count || 0);
        setTransferVendorId("");

        if (data.housekeepers_count > 0) {
          // Show modal with options
          setVendorDeleteModalOpen(true);
        } else {
          // No housekeepers, show simple confirmation
          showConfirmAlert(
            () => confirmDeleteVendor(vendor.uid, "delete_all"),
            "Are you sure you want to delete this vendor?"
          );
        }
      }
    } catch (error) {
      console.error("Error checking vendor:", error);
      toast.error("Failed to check vendor");
    }
  };

  // Confirm delete vendor with action
  const confirmDeleteVendor = async (uid, action, transferToVendorId = "") => {
    try {
      let url = `${BASE_URL}/staff/vendor?uid=${uid}&action=${action}`;
      if (action === "transfer" && transferToVendorId) {
        url += `&transfer_to_vendor_id=${transferToVendorId}`;
      }

      const response = await fetch(url, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${TOKEN}` },
      });
      const data = await response.json();

      if (data?.status) {
        toast.success(data.message || "Vendor deleted successfully");
        setVendorDeleteModalOpen(false);
        setVendorToDelete(null);
        fetchVendors();
        fetchHousekeeping(); // Refresh housekeeping list too
      } else {
        toast.error(data?.message || "Failed to delete vendor");
      }
    } catch (error) {
      console.error("Error deleting vendor:", error);
      toast.error("Failed to delete vendor");
    }
  };

  // Save Shifts
  const handleSaveShifts = async () => {
    const validShifts = shiftForm.shift_details.filter(
      (s) => s.shift_name && s.start_time && s.end_time
    );
    if (validShifts.length === 0) {
      toast.error("Please add at least one valid shift");
      return;
    }

    try {
      // API expects "shiftDetails" (camelCase), not "shift_details"
      const response = await fetch(`${BASE_URL}/staff/housekeeping/shift`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${TOKEN}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ shiftDetails: validShifts }),
      });
      const data = await response.json();
      if (data?.status) {
        toast.success("Shifts saved successfully");
        setShiftModalOpen(false);
        setShiftForm(initialShiftForm);
        fetchShifts();
      } else {
        toast.error(data?.message || "Failed to save shifts");
      }
    } catch (error) {
      console.error("Error saving shifts:", error);
      toast.error("Failed to save shifts");
    }
  };

  // Edit Housekeeping
  const handleEditHousekeeping = (row) => {
    setIsEdit(true);
    setEditData(row);
    setHousekeepingForm({
      name: row.name || "",
      mobile: row.mobile || "",
      gym_id: row.gym_id || "",
      vendor_id: row.vendor_id || "",
      shift_ids: row.shift_ids || [],
      address: row.address || "",
      salary: row.salary !== undefined && row.salary !== null ? String(row.salary) : "",
    });
    setHousekeepingModalOpen(true);
  };

  // View Attendance History
  const handleViewAttendance = async (row) => {
    setSelectedStaff(row);
    setCalendarViewDate(moment()); // Reset to current month
    setAttendanceModalOpen(true);
    fetchAttendanceData(row, moment());
  };

  // Fetch attendance data for a specific month
  const fetchAttendanceData = async (staff, viewDate) => {
    setAttendanceLoading(true);
    try {
      const dateFrom = viewDate.clone().startOf("month").format("YYYY-MM-DD");
      const dateTo = viewDate.clone().endOf("month").format("YYYY-MM-DD");

      // Fetch calendar data from /attendance endpoint (uses uid filter)
      const calendarResponse = await fetch(
        `${BASE_URL}/staff/housekeeping/attendance?date_from=${dateFrom}&date_to=${dateTo}&user_id=${staff.uid}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${TOKEN}` },
        }
      );
      const calendarData = await calendarResponse.json();

      // Fetch stats data from /attendance/logs endpoint (uses mobile filter)
      const statsResponse = await fetch(
        `${BASE_URL}/staff/housekeeping/attendance/logs?date_from=${dateFrom}&date_to=${dateTo}&user_id=${staff.mobile}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${TOKEN}` },
        }
      );
      const statsData = await statsResponse.json();

      // Set calendar data for the calendar view
      if (calendarData?.status && calendarData?.data) {
        setAttendanceData(calendarData.data);
      } else {
        setAttendanceData([]);
      }

      // Set stats data and calculate stats
      if (statsData?.status && statsData?.data) {
        setAttendanceLogs(statsData.data);
        calculateAttendanceStats(statsData.data, staff.shift_details);
      } else {
        setAttendanceLogs([]);
        setAttendanceStats({
          totalShifts: 0,
          totalCheckins: 0,
          onTime: 0,
          late: 0,
        });
      }
    } catch (error) {
      console.error("Error fetching attendance:", error);
      toast.error("Failed to fetch attendance data");
      setAttendanceData([]);
      setAttendanceLogs([]);
    } finally {
      setAttendanceLoading(false);
    }
  };

  // Navigate calendar month
  const handleCalendarNavigate = (direction) => {
    const newDate = direction === "prev"
      ? calendarViewDate.clone().subtract(1, "month")
      : calendarViewDate.clone().add(1, "month");
    setCalendarViewDate(newDate);
    if (selectedStaff) {
      fetchAttendanceData(selectedStaff, newDate);
    }
  };

  // Generate calendar days for a month
  const generateCalendarDays = (viewDate) => {
    const startOfMonth = viewDate.clone().startOf("month");
    const endOfMonth = viewDate.clone().endOf("month");
    const startOfCalendar = startOfMonth.clone().startOf("week");
    const endOfCalendar = endOfMonth.clone().endOf("week");

    const days = [];
    const current = startOfCalendar.clone();

    while (current.isSameOrBefore(endOfCalendar, "day")) {
      days.push({
        date: current.clone(),
        isCurrentMonth: current.isSame(viewDate, "month"),
        isToday: current.isSame(moment(), "day"),
      });
      current.add(1, "day");
    }

    return days;
  };

  // Get attendance for a specific date - /attendance returns flat records
  const getAttendanceForDate = (date) => {
    // API returns date in DD-MM-YYYY format
    const dateStr = date.format("DD-MM-YYYY");
    const dateStrAlt = date.format("YYYY-MM-DD");
    // Filter all attendance records for this date
    const records = attendanceData.filter((log) => log.date === dateStr || log.date === dateStrAlt);
    if (records.length > 0) {
      return { date: dateStr, records };
    }
    return null;
  };

  // Handle click on a calendar day to view details
  const handleDayClick = async (date, attendance) => {
    if (!selectedStaff) return;

    try {
      const dateStr = date.format("YYYY-MM-DD");
      const response = await fetch(
        `${BASE_URL}/staff/housekeeping/attendance/logs?date_from=${dateStr}&date_to=${dateStr}&user_id=${selectedStaff.mobile}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${TOKEN}` },
        }
      );
      const data = await response.json();

      if (data?.status && data?.data && data.data.length > 0) {
        setSelectedDayDetails({
          date: date.format("DD MMM YYYY"),
          shifts: data.data[0].shift_details || [],
        });
        setDayDetailsModalOpen(true);
      } else if (attendance?.records && attendance.records.length > 0) {
        // Fallback to flat attendance data if logs endpoint doesn't return shift_details
        setSelectedDayDetails({
          date: date.format("DD MMM YYYY"),
          records: attendance.records,
        });
        setDayDetailsModalOpen(true);
      }
    } catch (error) {
      console.error("Error fetching day details:", error);
    }
  };

  // Convert 12-hour format to 24-hour format for comparison
  const convertTo24HourFormat = (time12h) => {
    if (!time12h) return null;
    // If already in 24-hour format (no AM/PM)
    if (!time12h.includes('AM') && !time12h.includes('PM') && !time12h.includes('am') && !time12h.includes('pm')) {
      return time12h;
    }
    const [time, modifier] = time12h.split(' ');
    let [hours, minutes] = time.split(':');

    if (hours === '12') {
      hours = '00';
    }
    if (modifier?.toUpperCase() === 'PM') {
      hours = String(parseInt(hours, 10) + 12);
    }
    if (parseInt(hours) < 10 && hours.length === 1) {
      hours = '0' + hours;
    }
    return `${hours}:${minutes}`;
  };

  // Calculate attendance stats - matching Angular implementation
  const calculateAttendanceStats = (logs, shiftDetails) => {
    // Get totalShifts from first log's shift_details if available
    let totalShifts = 0;
    let totalCheckins = 0;
    let onTime = 0;
    let late = 0;

    if (logs.length > 0 && logs[0].shift_details) {
      totalShifts = logs[0].shift_details.length;

      // Iterate through all logs (each day)
      logs.forEach((log) => {
        if (log.shift_details) {
          log.shift_details.forEach((shift) => {
            // Use checkinLogs if available (Angular format), otherwise use attendance_logs
            const checkinLogs = shift.checkinLogs || shift.attendance_logs || [];

            checkinLogs.forEach((checkLog) => {
              // Only count "in" mode check-ins
              if (checkLog.mode === "in" || !checkLog.mode) {
                totalCheckins++;

                // Compare check-in time with shift start time
                const shiftStartTime = shift.start_time;
                let checkinTime = checkLog.time;

                // Convert to 24-hour format if needed
                checkinTime = convertTo24HourFormat(checkinTime);

                if (shiftStartTime && checkinTime) {
                  const shiftStart = new Date(`1970-01-01T${shiftStartTime}`);
                  const logTime = new Date(`1970-01-01T${checkinTime}`);

                  // If check-in time is less than or equal to shift start time, it's on time
                  if (logTime.getTime() <= shiftStart.getTime()) {
                    onTime++;
                  } else {
                    late++;
                  }
                }
              }
            });
          });
        }
      });
    } else {
      // Fallback: use shiftDetails from staff record
      totalShifts = shiftDetails?.length || 0;
    }

    setAttendanceStats({
      totalShifts,
      totalCheckins,
      onTime,
      late,
    });
  };

  // Add Housekeeping Modal Open
  const handleAddHousekeeping = () => {
    setIsEdit(false);
    setEditData(null);
    setHousekeepingForm(initialHousekeepingForm);
    setHousekeepingModalOpen(true);
  };

  // Clear filters
  const handleClearFilters = () => {
    setSearchInput("");
    setGymFilter("");
    setVendorFilter("");
    setIsPage(0);
  };

  // Add shift row
  const addShiftRow = () => {
    setShiftForm((prev) => ({
      ...prev,
      shift_details: [
        ...prev.shift_details,
        { shift_name: "", start_time: "", end_time: "", status: "active" },
      ],
    }));
  };

  // Remove shift row
  const removeShiftRow = (index) => {
    setShiftForm((prev) => ({
      ...prev,
      shift_details: prev.shift_details.filter((_, i) => i !== index),
    }));
  };

  // Update shift row
  const updateShiftRow = (index, field, value) => {
    setShiftForm((prev) => ({
      ...prev,
      shift_details: prev.shift_details.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  // Handle shift selection
  const handleShiftSelection = (shiftId) => {
    setHousekeepingForm((prev) => {
      const currentShifts = prev.shift_ids || [];
      if (currentShifts.includes(shiftId)) {
        return { ...prev, shift_ids: currentShifts.filter((id) => id !== shiftId) };
      } else {
        return { ...prev, shift_ids: [...currentShifts, shiftId] };
      }
    });
  };

  // Housekeeping Table Columns
  const housekeepingColumns = [
    {
      dataField: "name",
      text: "Staff Details",
      formatter: (_, row) => (
        <div className="emeberDetailsRowContainer">
          <div className="d-flex flex-column gap-1">
            <span className="tb-data fw-semibold">{row?.name || "NA"}</span>
            <div className="d-flex align-items-center gap-2">
              <CallIcon color="#6c757d" />
              <small className="tb-data small">{row?.mobile || "NA"}</small>
            </div>
          </div>
        </div>
      ),
    },
    {
      dataField: "gym_name",
      text: "Gym",
      formatter: (_, row) => (
        <span className="tb-data">
          <span className="badge bg-light text-dark border">{row?.gym_name || "NA"}</span>
        </span>
      ),
    },
    {
      dataField: "vendor_name",
      text: "Vendor",
      formatter: (_, row) => (
        <span className="tb-data">
          <span className="small">{row?.vendor_name || row?.agency_name || "NA"}</span>
        </span>
      ),
    },
    {
      dataField: "shift_details",
      text: "Shift Details",
      formatter: (_, row) => (
        <div className="tb-data shift-details-cell">
          {row?.shift_details && row.shift_details.length > 0 ? (
            <table className="shift-details-table">
              <thead>
                <tr>
                  <th>Shift Name</th>
                  <th>Start Time</th>
                  <th>End Time</th>
                  <th>First Check-in</th>
                  <th>Last Check-in</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {row.shift_details.map((shift, idx) => {
                  const lateStatus = getLateStatus(shift.attendance_logs, shift.start_time);
                  return (
                    <tr key={idx}>
                      <td>{shift.shift_name}</td>
                      <td>{formatTime12Hour(shift.start_time)}</td>
                      <td>{formatTime12Hour(shift.end_time)}</td>
                      <td>
                        <strong>{getFirstAndLastCheckin(shift.attendance_logs, "first")}</strong>
                      </td>
                      <td>
                        <strong>{getFirstAndLastCheckin(shift.attendance_logs, "last")}</strong>
                      </td>
                      <td>
                        {lateStatus.isLate !== null && (
                          <span className={`late-badge ${lateStatus.isLate ? "late" : "early"}`}>
                            {lateStatus.text}
                          </span>
                        )}
                        {lateStatus.isLate === null && shift.attendance_logs?.length === 0 && (
                          <span className="late-badge na">N/A</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            "No Shifts"
          )}
        </div>
      ),
    },
    {
      dataField: "salary",
      text: "Salary",
      formatter: (_, row) => (
        <span className="tb-data">
          <span className="small">₹{row?.salary || 0}/month</span>
        </span>
      ),
    },
    {
      dataField: "action",
      text: "Actions",
      formatter: (_, row) => (
        <div className="actionButtonContainer">
          <button
            className="actionBtn icon edit"
            onClick={() => handleEditHousekeeping(row)}
            title="Edit"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708l-3-3zm.646 6.061L9.793 2.5 3.293 9H3.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.207l6.5-6.5zm-7.468 7.468A.5.5 0 0 1 6 13.5V13h-.5a.5.5 0 0 1-.5-.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.5-.5V10h-.5a.499.499 0 0 1-.175-.032l-.179.178a.5.5 0 0 0-.11.168l-2 5a.5.5 0 0 0 .65.65l5-2a.5.5 0 0 0 .168-.11l.178-.178z" />
            </svg>
          </button>
          <button
            className="actionBtn icon delete"
            onClick={() => handleDeleteHousekeeping(row?.uid)}
            title="Delete"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
              <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z" />
            </svg>
          </button>
          <button
            className="actionBtn icon calendar"
            onClick={() => handleViewAttendance(row)}
            title="View Attendance History"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z" />
              <path d="M6.445 11.688V6.354h-.633A12.6 12.6 0 0 0 4.5 7.16v.695c.375-.257.969-.62 1.258-.777h.012v4.61h.675zm1.188-1.305c.047.64.594 1.406 1.703 1.406 1.258 0 2-1.066 2-2.871 0-1.934-.781-2.668-1.953-2.668-.926 0-1.797.672-1.797 1.809 0 1.16.824 1.77 1.676 1.77.746 0 1.23-.376 1.383-.79h.027c-.004 1.316-.461 2.164-1.305 2.164-.664 0-1.008-.45-1.05-.82h-.684zm2.953-2.317c0 .696-.559 1.18-1.184 1.18-.601 0-1.144-.383-1.144-1.2 0-.823.582-1.21 1.168-1.21.633 0 1.16.398 1.16 1.23z" />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  // Vendor Table Columns
  const vendorColumns = [
    {
      dataField: "name",
      text: "Vendor Details",
      formatter: (_, row) => (
        <div className="emeberDetailsRowContainer">
          <div className="d-flex flex-column gap-1">
            <span className="tb-data fw-semibold">{row?.name || "NA"}</span>
            <div className="d-flex align-items-center gap-2">
              <CallIcon color="#6c757d" />
              <small className="tb-data small">{row?.mobile || "NA"}</small>
            </div>
            <div className="d-flex align-items-center gap-2">
              <EmailIcon color="#6c757d" />
              <small className="tb-data small" style={{ textTransform: "lowercase" }}>
                {row?.email || "NA"}
              </small>
            </div>
          </div>
        </div>
      ),
    },
    {
      dataField: "agency_name",
      text: "Agency Name",
      formatter: (_, row) => (
        <span className="tb-data">
          <span className="badge bg-light text-dark border">{row?.agency_name || "NA"}</span>
        </span>
      ),
    },
    {
      dataField: "address",
      text: "Address",
      formatter: (_, row) => (
        <span className="tb-data">
          <span className="small text-muted">{row?.address || "NA"}</span>
        </span>
      ),
    },
    {
      dataField: "status",
      text: "Status",
      formatter: (_, row) => {
        const isActive = row?.status === "active";
        return (
          <span className="tb-data">
            <span
              className="badge"
              style={{
                backgroundColor: isActive ? "#d1fae5" : "#fee2e2",
                color: isActive ? "#065f46" : "#991b1b",
                padding: "6px 12px",
                borderRadius: "6px",
                fontWeight: "500",
                textTransform: "capitalize",
              }}
            >
              {row?.status || "NA"}
            </span>
          </span>
        );
      },
    },
    {
      dataField: "date_added",
      text: "Date Added",
      formatter: (_, row) => (
        <span className="tb-data">
          <span className="small text-muted">
            {row?.date_added
              ? moment(row.date_added).tz("Asia/Kolkata").format("DD-MM-YYYY")
              : "NA"}
          </span>
        </span>
      ),
    },
    {
      dataField: "action",
      text: "Actions",
      formatter: (_, row) => (
        <div className="actionButtonContainer">
          <button
            className="actionBtn icon delete"
            onClick={() => handleDeleteVendor(row)}
            title="Delete Vendor"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
              <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z" />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  // Prepare dropdown options
  const gymOptions = gymData.map((gym) => ({
    value: gym.user_id,
    label: gym.gym_name,
  }));

  const vendorOptions = vendorData.map((vendor) => ({
    value: vendor.uid,
    label: `${vendor.name} - ${vendor.agency_name}`,
  }));

  // Get selected option objects for dropdowns
  const selectedGymOption = gymOptions.find((opt) => opt.value === gymFilter) || null;
  const selectedVendorOption = vendorOptions.find((opt) => opt.value === vendorFilter) || null;

  // Render Filters
  const renderHousekeepingFilters = () => (
    <Row className="mb-4">
      <Col>
        <FilterContainer onReset={handleClearFilters}>
          <SearchFilter
            selectedSearch={searchInput}
            setSelectedSearch={setSearchInput}
          />
          <CustomDropDownFilter
            options={gymOptions}
            selectedOption={selectedGymOption}
            setSelectedOption={(selected) => {
              setGymFilter(selected?.value || "");
              setIsPage(0);
            }}
            name="All Gyms"
          />
          <CustomDropDownFilter
            options={vendorOptions}
            selectedOption={selectedVendorOption}
            setSelectedOption={(selected) => {
              setVendorFilter(selected?.value || "");
              setIsPage(0);
            }}
            name="All Vendors"
          />
        </FilterContainer>
      </Col>
    </Row>
  );

  return (
    <>
      <Row>
        <Col>
          <PageHeadingNew
            heading="Housekeeping Onboard"
            buttonLabel={activeTab === "housekeeping" ? "Add Housekeeping" : "Add Vendor"}
            onButtonClick={
              activeTab === "housekeeping"
                ? handleAddHousekeeping
                : () => setVendorModalOpen(true)
            }
            buttonLabel1={activeTab === "housekeeping" ? "Manage Shifts" : null}
            onButtonClick1={activeTab === "housekeeping" ? () => setShiftModalOpen(true) : null}
          />
        </Col>
      </Row>

      {/* Tabs */}
      <Row className="mb-4">
        <Col>
          <div className="housekeeping-tabs">
            <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
              <Nav variant="tabs" className="housekeeping-nav-tabs">
                <Nav.Item>
                  <Nav.Link eventKey="housekeeping">Housekeeping Staff</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="vendors">Vendors</Nav.Link>
                </Nav.Item>
              </Nav>
            </Tab.Container>
          </div>
        </Col>
      </Row>

      {/* Housekeeping Tab Content */}
      {activeTab === "housekeeping" && (
        <>
          {renderHousekeepingFilters()}
          <Row>
            <Col>
              <div className="fullWidthFlexCenter overflowAuto separatedTable no-border-table mod-height-100vh">
                {loading ? (
                  <TableSkeleton />
                ) : (
                  <PaymentLinkTable
                    tableData={housekeepingData || []}
                    tableColumns={housekeepingColumns}
                    tablePagination={pagination}
                    isPage={isPage}
                    setIsPage={(value) => setIsPage(value)}
                    pageLimit={pageLimit}
                    setPageLimit={setPageLimit}
                  />
                )}
              </div>
            </Col>
          </Row>
        </>
      )}

      {/* Vendors Tab Content */}
      {activeTab === "vendors" && (
        <Row>
          <Col>
            <div className="fullWidthFlexCenter overflowAuto separatedTable no-border-table mod-height-100vh">
              {vendorLoading ? (
                <TableSkeleton />
              ) : (
                <PaymentLinkTable
                  tableData={vendorData || []}
                  tableColumns={vendorColumns}
                  tablePagination={vendorPagination}
                  isPage={vendorPage}
                  setIsPage={setVendorPage}
                  pageLimit={vendorPageLimit}
                  setPageLimit={setVendorPageLimit}
                />
              )}
            </div>
          </Col>
        </Row>
      )}

      {/* Add/Edit Housekeeping Modal */}
      <Modal
        isOpen={housekeepingModalOpen}
        onRequestClose={() => setHousekeepingModalOpen(false)}
        contentLabel="Housekeeping Modal"
        ariaHideApp={false}
        style={{
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            zIndex: 1050,
            overflow: "auto",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            padding: "40px 20px",
          },
          content: {
            position: "relative",
            inset: "unset",
            border: "none",
            background: "transparent",
            overflow: "visible",
            padding: 0,
          },
        }}
      >
        <div className="createNewCTOModalContainer housekeeping-modal-content">
          <ModalHeadAndClose
            title={isEdit ? "Edit Housekeeping Staff" : "Add Housekeeping Staff"}
            onClose={() => setHousekeepingModalOpen(false)}
          />
          <div className="housekeeping-form">
            <div className="form-row">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={housekeepingForm.name}
                  onChange={(e) =>
                    setHousekeepingForm({ ...housekeepingForm, name: e.target.value })
                  }
                  placeholder="Enter name"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Mobile *</label>
                <input
                  type="text"
                  value={housekeepingForm.mobile}
                  onChange={(e) =>
                    setHousekeepingForm({ ...housekeepingForm, mobile: e.target.value })
                  }
                  placeholder="Enter mobile"
                  className="form-input"
                  disabled={isEdit}
                  maxLength={10}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Gym *</label>
                <Select
                  value={gymData
                    .map((gym) => ({ value: gym.user_id, label: gym.gym_name }))
                    .find((opt) => opt.value === housekeepingForm.gym_id) || null}
                  onChange={(selected) =>
                    setHousekeepingForm({ ...housekeepingForm, gym_id: selected?.value || "" })
                  }
                  options={gymData.map((gym) => ({ value: gym.user_id, label: gym.gym_name }))}
                  placeholder="Search & Select Gym"
                  isClearable
                  isSearchable
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    control: (base, state) => ({
                      ...base,
                      minHeight: "45px",
                      borderRadius: "8px",
                      borderColor: state.isFocused ? "#cd0909" : "#ddd",
                      boxShadow: state.isFocused ? "0 0 0 3px rgba(205, 9, 9, 0.1)" : "none",
                      "&:hover": { borderColor: "#cd0909" },
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected ? "#cd0909" : state.isFocused ? "#fff5f5" : "white",
                      color: state.isSelected ? "white" : "#1a2233",
                    }),
                  }}
                />
              </div>
              <div className="form-group">
                <label>Vendor *</label>
                <Select
                  value={vendorData
                    .map((vendor) => ({ value: vendor.uid, label: `${vendor.name} - ${vendor.agency_name}` }))
                    .find((opt) => opt.value === housekeepingForm.vendor_id) || null}
                  onChange={(selected) =>
                    setHousekeepingForm({ ...housekeepingForm, vendor_id: selected?.value || "" })
                  }
                  options={vendorData.map((vendor) => ({ value: vendor.uid, label: `${vendor.name} - ${vendor.agency_name}` }))}
                  placeholder="Search & Select Vendor"
                  isClearable
                  isSearchable
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    control: (base, state) => ({
                      ...base,
                      minHeight: "45px",
                      borderRadius: "8px",
                      borderColor: state.isFocused ? "#cd0909" : "#ddd",
                      boxShadow: state.isFocused ? "0 0 0 3px rgba(205, 9, 9, 0.1)" : "none",
                      "&:hover": { borderColor: "#cd0909" },
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected ? "#cd0909" : state.isFocused ? "#fff5f5" : "white",
                      color: state.isSelected ? "white" : "#1a2233",
                    }),
                  }}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Salary (per month)</label>
                <input
                  type="number"
                  value={housekeepingForm.salary}
                  onChange={(e) =>
                    setHousekeepingForm({
                      ...housekeepingForm,
                      salary: e.target.value,
                    })
                  }
                  placeholder="Enter salary"
                  className="form-input"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={housekeepingForm.address}
                  onChange={(e) =>
                    setHousekeepingForm({ ...housekeepingForm, address: e.target.value })
                  }
                  placeholder="Enter address"
                  className="form-input"
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="form-group full-width">
              <label>Select Shifts</label>
              <div className="shift-selection">
                {shiftData.map((shift) => (
                  <label key={shift.uid} className="shift-checkbox">
                    <input
                      type="checkbox"
                      checked={housekeepingForm.shift_ids?.includes(shift.uid)}
                      onChange={() => handleShiftSelection(shift.uid)}
                    />
                    <span>
                      <strong>{shift.shift_name}</strong>
                      <small style={{ color: "#6c757d" }}>
                        {formatTime12Hour(shift.start_time)} - {formatTime12Hour(shift.end_time)}
                      </small>
                    </span>
                  </label>
                ))}
                {shiftData.length === 0 && (
                  <p className="no-shifts-text">No shifts available. Please create shifts first.</p>
                )}
              </div>
            </div>
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setHousekeepingModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleSaveHousekeeping}
              >
                {isEdit ? "Update" : "Add"}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Add Vendor Modal */}
      <Modal
        isOpen={vendorModalOpen}
        onRequestClose={() => setVendorModalOpen(false)}
        contentLabel="Vendor Modal"
        ariaHideApp={false}
        style={{
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            zIndex: 1050,
            overflow: "auto",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            padding: "40px 20px",
          },
          content: {
            position: "relative",
            inset: "unset",
            border: "none",
            background: "transparent",
            overflow: "visible",
            padding: 0,
          },
        }}
      >
        <div className="createNewCTOModalContainer housekeeping-modal-content">
          <ModalHeadAndClose
            title="Add Vendor"
            onClose={() => setVendorModalOpen(false)}
          />
          <div className="housekeeping-form">
            <div className="form-row">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={vendorForm.name}
                  onChange={(e) => setVendorForm({ ...vendorForm, name: e.target.value })}
                  placeholder="Enter name"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Mobile *</label>
                <input
                  type="text"
                  value={vendorForm.mobile}
                  onChange={(e) => setVendorForm({ ...vendorForm, mobile: e.target.value })}
                  placeholder="Enter mobile"
                  className="form-input"
                  maxLength={10}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={vendorForm.email}
                  onChange={(e) => setVendorForm({ ...vendorForm, email: e.target.value })}
                  placeholder="Enter email"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Agency Name</label>
                <input
                  type="text"
                  value={vendorForm.agency_name}
                  onChange={(e) =>
                    setVendorForm({ ...vendorForm, agency_name: e.target.value })
                  }
                  placeholder="Enter agency name"
                  className="form-input"
                />
              </div>
            </div>
            <div className="form-group full-width">
              <label>Address</label>
              <textarea
                value={vendorForm.address}
                onChange={(e) => setVendorForm({ ...vendorForm, address: e.target.value })}
                placeholder="Enter address"
                className="form-input"
                rows="3"
                autoComplete="off"
              />
            </div>
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setVendorModalOpen(false)}
              >
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={handleAddVendor}>
                Add Vendor
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Manage Shifts Modal */}
      <Modal
        isOpen={shiftModalOpen}
        onRequestClose={() => setShiftModalOpen(false)}
        contentLabel="Shifts Modal"
        ariaHideApp={false}
        style={{
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            zIndex: 1050,
            overflow: "auto",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            padding: "40px 20px",
          },
          content: {
            position: "relative",
            inset: "unset",
            border: "none",
            background: "transparent",
            overflow: "visible",
            padding: 0,
          },
        }}
      >
        <div className="createNewCTOModalContainer housekeeping-modal-content shifts-modal-content">
          <ModalHeadAndClose
            title="Manage Shifts"
            onClose={() => setShiftModalOpen(false)}
          />
          <div className="housekeeping-form">
            <div className="shifts-container">
              <h4>Create New Shifts</h4>
              {shiftForm.shift_details.map((shift, index) => (
                <div key={index} className="shift-row">
                  <div className="shift-field shift-field-name">
                    <label>Shift Name</label>
                    <input
                      type="text"
                      value={shift.shift_name}
                      onChange={(e) => updateShiftRow(index, "shift_name", e.target.value)}
                      placeholder="e.g. Morning"
                      className="form-input"
                    />
                  </div>
                  <div className="shift-field">
                    <label>Start Time</label>
                    <input
                      type="time"
                      value={shift.start_time}
                      onChange={(e) => updateShiftRow(index, "start_time", e.target.value)}
                      className="form-input"
                    />
                  </div>
                  <div className="shift-field">
                    <label>End Time</label>
                    <input
                      type="time"
                      value={shift.end_time}
                      onChange={(e) => updateShiftRow(index, "end_time", e.target.value)}
                      className="form-input"
                    />
                  </div>
                  <div className="shift-row-actions">
                    {shiftForm.shift_details.length > 1 && (
                      <button
                        type="button"
                        className="shift-icon-btn remove"
                        title="Remove shift"
                        onClick={() => removeShiftRow(index)}
                      >
                        −
                      </button>
                    )}
                    {index === shiftForm.shift_details.length - 1 && (
                      <button
                        type="button"
                        className="shift-icon-btn add"
                        title="Add another shift"
                        onClick={addShiftRow}
                      >
                        +
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {shiftData.length > 0 && (
              <div className="existing-shifts">
                <h4>Existing Shifts</h4>
                <div className="existing-shifts-list">
                  {shiftData.map((shift) => (
                    <div key={shift.uid} className="existing-shift-item">
                      <span className="shift-name">{shift.shift_name}</span>
                      <span className="shift-time">
                        {formatTime12Hour(shift.start_time)} - {formatTime12Hour(shift.end_time)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShiftModalOpen(false)}
              >
                Close
              </button>
              <button type="button" className="btn btn-danger" onClick={handleSaveShifts}>
                Save Shifts
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Attendance History Modal */}
      <Modal
        isOpen={attendanceModalOpen}
        onRequestClose={() => setAttendanceModalOpen(false)}
        contentLabel="Attendance History Modal"
        ariaHideApp={false}
        style={{
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            zIndex: 1050,
            overflow: "auto",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            padding: "40px 20px",
          },
          content: {
            position: "relative",
            inset: "unset",
            border: "none",
            background: "transparent",
            overflow: "visible",
            padding: 0,
          },
        }}
      >
        <div className="createNewCTOModalContainer housekeeping-modal-content attendance-modal-content">
          <ModalHeadAndClose
            title="Attendance History"
            onClose={() => setAttendanceModalOpen(false)}
          />

          {selectedStaff && (
            <div className="attendance-staff-info">
              <div className="staff-info-row">
                <span className="staff-name">{selectedStaff.name}</span>
                <span className="staff-mobile">{selectedStaff.mobile}</span>
                <span className="staff-gym badge bg-light text-dark border">
                  {selectedStaff.gym_name}
                </span>
              </div>
            </div>
          )}

          {/* Stats Cards */}
          <div className="attendance-stats-grid">
            <div className="attendance-stat-card">
              <span className="stat-value">{attendanceStats.totalShifts}</span>
              <span className="stat-label">Total Shifts</span>
            </div>
            <div className="attendance-stat-card">
              <span className="stat-value">{attendanceStats.totalCheckins}</span>
              <span className="stat-label">Total Check-ins</span>
            </div>
            <div className="attendance-stat-card success">
              <span className="stat-value">{attendanceStats.onTime}</span>
              <span className="stat-label">On Time</span>
            </div>
            <div className="attendance-stat-card danger">
              <span className="stat-value">{attendanceStats.late}</span>
              <span className="stat-label">Late Check-ins</span>
            </div>
          </div>

          {/* Calendar Navigation */}
          <div className="calendar-navigation">
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => handleCalendarNavigate("prev")}
            >
              &lt; Previous
            </button>
            <h4 className="calendar-month-title">
              {calendarViewDate.format("MMMM YYYY")}
            </h4>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => handleCalendarNavigate("next")}
            >
              Next &gt;
            </button>
          </div>

          {/* Calendar View */}
          <div className="attendance-calendar-container">
            {attendanceLoading ? (
              <div className="attendance-loading">
                <span>Loading attendance data...</span>
              </div>
            ) : (
              <div className="attendance-calendar-grid">
                {/* Calendar Header Cells */}
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day, idx) => (
                  <div
                    key={day}
                    className={`calendar-header-cell ${idx === 0 || idx === 6 ? 'weekend' : ''}`}
                  >
                    {day}
                  </div>
                ))}

                {/* Calendar Body Cells */}
                {generateCalendarDays(calendarViewDate).map((day, index) => {
                  const attendance = getAttendanceForDate(day.date);
                  const hasAttendance = attendance && attendance.records && attendance.records.length > 0;
                  const dayOfWeek = day.date.day();
                  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

                  return (
                    <div
                      key={index}
                      className={`calendar-day-cell ${!day.isCurrentMonth ? "other-month" : ""} ${day.isToday ? "today" : ""} ${hasAttendance ? "has-attendance" : ""}`}
                      onClick={() => hasAttendance && handleDayClick(day.date, attendance)}
                      style={{ cursor: hasAttendance ? "pointer" : "default" }}
                    >
                      <span className={`day-number ${day.isToday ? "today-number" : ""} ${isWeekend && !day.isToday ? "weekend" : ""}`}>
                        {day.date.date()}
                      </span>
                      {day.isCurrentMonth && hasAttendance && (
                        <div className="calendar-attendance-badge">
                          <span className="attendance-name" title={`Click to view ${selectedStaff?.name}'s attendance details`}>
                            {selectedStaff?.name}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setAttendanceModalOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      </Modal>

      {/* Day Details Modal */}
      <Modal
        isOpen={dayDetailsModalOpen}
        onRequestClose={() => setDayDetailsModalOpen(false)}
        contentLabel="Day Details Modal"
        ariaHideApp={false}
        style={{
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            zIndex: 1060,
            overflow: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px 20px",
          },
          content: {
            position: "relative",
            inset: "unset",
            border: "none",
            background: "transparent",
            overflow: "visible",
            padding: 0,
          },
        }}
      >
        <div className="createNewCTOModalContainer housekeeping-modal-content" style={{ minWidth: "500px" }}>
          <ModalHeadAndClose
            title="Attendance Details"
            onClose={() => setDayDetailsModalOpen(false)}
          />

          {selectedDayDetails && (
            <>
              <div className="attendance-staff-info">
                <div className="staff-info-row">
                  <span className="staff-name">{selectedStaff?.name}</span>
                  <span className="staff-mobile">{selectedStaff?.mobile}</span>
                </div>
                <div style={{ marginTop: "0.5rem" }}>
                  <span className="badge bg-primary">{selectedDayDetails.date}</span>
                </div>
              </div>

              <div className="attendance-logs-container" style={{ maxHeight: "400px" }}>
                {selectedDayDetails.shifts && selectedDayDetails.shifts.length > 0 ? (
                  <table className="attendance-logs-table">
                    <thead>
                      <tr>
                        <th>Shift Name</th>
                        <th>Shift Time</th>
                        <th>Check-in Times</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedDayDetails.shifts.map((shift, idx) => {
                        const checkins = shift.checkinLogs || shift.attendance_logs || [];
                        return (
                          <tr key={idx}>
                            <td><strong>{shift.shift_name}</strong></td>
                            <td>{formatTime12Hour(shift.start_time)} - {formatTime12Hour(shift.end_time)}</td>
                            <td>
                              {checkins.length > 0 ? (
                                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                  {checkins.map((log, logIdx) => (
                                    <span key={logIdx} className="badge bg-success" style={{ fontSize: "12px" }}>
                                      {log.mode ? `${log.mode}: ` : ""}{formatTime12Hour(log.time)}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="badge bg-secondary">No check-ins</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : selectedDayDetails.records && selectedDayDetails.records.length > 0 ? (
                  <table className="attendance-logs-table">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Role</th>
                        <th>Gym</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedDayDetails.records.map((record, idx) => (
                        <tr key={idx}>
                          <td>
                            <span className="badge bg-success" style={{ fontSize: "12px" }}>
                              {formatTime12Hour(record.time)}
                            </span>
                          </td>
                          <td>{record.role || "Housekeeping"}</td>
                          <td>{record.gym_name || "N/A"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-attendance-data">No attendance data available</div>
                )}
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setDayDetailsModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Vendor Delete Confirmation Modal */}
      <Modal
        isOpen={vendorDeleteModalOpen}
        onRequestClose={() => setVendorDeleteModalOpen(false)}
        contentLabel="Delete Vendor Modal"
        ariaHideApp={false}
        style={{
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            zIndex: 1050,
            overflow: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px 20px",
          },
          content: {
            position: "relative",
            inset: "unset",
            border: "none",
            background: "transparent",
            overflow: "visible",
            padding: 0,
          },
        }}
      >
        <div className="createNewCTOModalContainer housekeeping-modal-content vendor-delete-modal">
          <ModalHeadAndClose
            title="Delete Vendor"
            onClose={() => setVendorDeleteModalOpen(false)}
          />

          <div className="vendor-delete-warning">
            <div className="warning-icon">
              <svg width="48" height="48" viewBox="0 0 16 16" fill="#f59e0b">
                <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
              </svg>
            </div>
            <h4 className="warning-title">Vendor Has Housekeepers</h4>
            <p className="warning-text">
              <strong>{vendorToDelete?.name}</strong> has{" "}
              <strong>{housekeeperCount}</strong> active housekeeper(s)
            </p>
            <p className="warning-subtext">
              Choose how to handle them before deleting this vendor
            </p>
          </div>

          <div className="vendor-delete-options">
            {/* Option 1: Transfer (Recommended) */}
            <div className="delete-option transfer-option">
              <label className="transfer-label">Transfer housekeepers to another vendor:</label>
              <select
                value={transferVendorId}
                onChange={(e) => setTransferVendorId(e.target.value)}
                className="form-input"
              >
                <option value="">Select vendor to transfer housekeepers...</option>
                {vendorData
                  .filter((v) => v.uid !== vendorToDelete?.uid)
                  .map((vendor) => (
                    <option key={vendor.uid} value={vendor.uid}>
                      {vendor.name} {vendor.agency_name ? `- ${vendor.agency_name}` : ""}
                    </option>
                  ))}
              </select>
              <button
                type="button"
                className="btn btn-success w-100"
                disabled={!transferVendorId}
                onClick={() =>
                  confirmDeleteVendor(vendorToDelete?.uid, "transfer", transferVendorId)
                }
              >
                Transfer & Delete Vendor
              </button>
              <small className="option-description">
                Housekeepers will be safely moved to the selected vendor
              </small>
            </div>

            {/* Divider */}
            <div className="option-divider">
              <span>or</span>
            </div>

            {/* Option 2: Delete All */}
            <div className="delete-option">
              <button
                type="button"
                className="btn btn-danger w-100"
                onClick={() => confirmDeleteVendor(vendorToDelete?.uid, "delete_all")}
              >
                Delete Vendor & All Housekeepers
              </button>
              <small className="option-description">
                Permanently remove vendor and all {housekeeperCount} housekeeper(s)
              </small>
            </div>
          </div>

          <div className="vendor-delete-cancel">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setVendorDeleteModalOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default HousekeepingOnboardStructure;
